import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConsts } from '@shared/commonHelper/AppConsts';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    AskAdminResult,
    BillingPlanName,
    BillingStatus,
    ChangePlanResult,
    CheckoutSession,
    CheckoutStatus,
    PlanChangeOptions,
    PlanSelection,
    StripeHandleResult,
} from './billing.models';

interface AbpResult<T> {
    result: T;
}
function unwrap<T>(response: T | AbpResult<T>): T {
    return response && typeof response === 'object' && 'result' in response
        ? (response as AbpResult<T>).result
        : (response as T);
}

/** The message to show for a failed call: the server's own (ABP's error envelope, or a failure the Stripe endpoints put in
 * their body), else the fallback. */
export function errorMessageOf(error: unknown, fallback: string): string {
    const body: unknown = error instanceof HttpErrorResponse ? error.error : null;
    const envelope = body && typeof body === 'object' ? (body as { error?: { message?: unknown } }).error : null;

    if (envelope && typeof envelope.message === 'string' && envelope.message) {
        return envelope.message;
    }
    return error instanceof Error && error.message ? error.message : fallback;
}

/** BillingAppService (/api/services/app/Billing) and the existing "Keep my plan" endpoint on StripeController. */
@Injectable({ providedIn: 'root' })
export class BillingApiService {
    private readonly url = AppConsts.remoteServiceBaseUrl + '/api/services/app/Billing/';

    constructor(private http: HttpClient) {}

    getStatus(): Observable<BillingStatus> {
        return this.http.get<AbpResult<BillingStatus> | BillingStatus>(this.url + 'GetStatus').pipe(map(unwrap));
    }

    createCheckoutSession(selection: PlanSelection, fromTeams: boolean): Observable<CheckoutSession> {
        return this.http
            .post<AbpResult<CheckoutSession> | CheckoutSession>(this.url + 'CreateCheckoutSession', {
                plan: selection.plan,
                people: selection.people,
                fromTeams,
            })
            .pipe(map(unwrap));
    }

    getCheckoutStatus(sessionId: string): Observable<CheckoutStatus> {
        return this.http
            .get<AbpResult<CheckoutStatus> | CheckoutStatus>(this.url + 'GetCheckoutStatus', { params: { sessionId } })
            .pipe(map(unwrap));
    }

    createBillingPortalSession(): Observable<string> {
        return this.http
            .post<AbpResult<{ url: string }> | { url: string }>(this.url + 'CreateBillingPortalSession', {})
            .pipe(map(unwrap), map(output => output.url));
    }

    askAdminToUpgrade(): Observable<AskAdminResult> {
        return this.http.post<AbpResult<AskAdminResult> | AskAdminResult>(this.url + 'AskAdminToUpgrade', {}).pipe(map(unwrap));
    }

    markUpgradePromptShown(daysMark: number): Observable<void> {
        return this.http.post<void>(this.url + 'MarkUpgradePromptShown', { daysMark });
    }

    getPlanChangeOptions(): Observable<PlanChangeOptions> {
        return this.http.get<AbpResult<PlanChangeOptions> | PlanChangeOptions>(this.url + 'GetPlanChangeOptions').pipe(map(unwrap));
    }

    changePlan(plan: BillingPlanName): Observable<ChangePlanResult> {
        return this.http.post<AbpResult<ChangePlanResult> | ChangePlanResult>(this.url + 'ChangePlan', { plan }).pipe(map(unwrap));
    }

    /** "Keep my plan": undoes a cancel-at-period-end (StripeController reactivate-subscription). */
    keepPlan(): Observable<void> {
        return this.http
            .post<StripeHandleResult | AbpResult<StripeHandleResult>>(AppConsts.remoteServiceBaseUrl + '/api/Stripe/reactivate-subscription', {})
            .pipe(
                map(response => {
                    const result = unwrap(response);

                    // The endpoint answers 200 with isError on failure: without this the banner said the plan was kept.
                    if (result && result.isError) {
                        throw new Error(result.errorMessage || 'Your plan could not be kept. Please try again.');
                    }
                })
            );
    }
}
